/**
 * useLiveTagData — resolves a dashboard widget's TAG bindings to LIVE readings.
 *
 * The gap this closes: dashboard widgets historically rendered against
 * fixtures (or imported datasets) and never read the live `readings` the
 * simulator/ingester writes. This hook makes a tag-bound widget show real
 * data in live mode:
 *   1. fetch the org's tags (cached) → map by lower-cased name,
 *   2. resolve each bound key (the picker stores the tag NAME) → tag id; a
 *      port the machine's roles resolve reads its port's own tag by id
 *      (B6: port-binding.ts, resolveBoundTags), and a port fed by several
 *      sources reads its one series (one series per port, S3: the port's
 *      series route, never one source's tag),
 *   3. poll each resolved tag's (or port's) latest value + recent series (~7s),
 *   4. return a `{ lowerKey → {latest, series, unit, …} }` map the widget
 *      resolvers prefer over fixtures.
 *
 * Stub mode (or port/entity bindings, or unresolved keys) → `hasLive:false`
 * and an empty map, so callers fall through to the existing behaviour.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { isLiveApi, listOrgTags, tags, type TagOption, type TagValuesPoint } from "@/lib/api";
import { readPortSeries, type PortSeries } from "@/lib/api/port-series";
import { plantDayStartMs, nextPlantDay } from "@/lib/plant-day-start";
import { plantTimeApi, type PlantTime } from "@/lib/shift-calendar";
import type { TimeseriesPoint } from "@/lib/widgets/types";
import { bucketWidthMs } from "@/lib/widgets/timeseries/time-axis";
import { shownFor } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import type { DataSource } from "./types";
import { boundKeysOf, type PortSeriesRef, type PortTags } from "./port-binding";

export interface LiveTagDatum {
  tagId: string;
  name: string;
  /**
   * The reading in the plant's words (the tag list's label, the machine page's name for it); absent from an older
   * server, when charts print `name`. See reading-names.ts.
   */
  label?: string;
  /** The machine the reading is bound to, when one is. */
  machine?: string;
  unit: string;
  dataKind: string;
  /** Latest numeric value, or null when the tag has no readings yet. */
  latest: number | null;
  /** ISO timestamp of the latest reading, or null when none — lets a table
   *  widget show WHEN a sparse reading last arrived (the series window can be
   *  empty for infrequent sensor tags, but /latest is age-independent). */
  latestTime: string | null;
  /** Recent samples (oldest→newest) for trend / chart rendering. */
  series: TimeseriesPoint[];
  /** The width of the buckets `series` holds the means of, in ms; unset when the read gave each raw reading. */
  bucketMs?: number;
  /**
   * An enum or label tag's latest label, or null when it has none; unset for a numeric tag. `latestTime` is then that
   * label's time. The batched latest carries no text, so the label is read from /values around the last reading.
   */
  latestLabel?: string | null;
  /**
   * Set only for a port read as its series (one series per port, S3) that is not decided yet: its older part is read
   * from the port's first choice alone, so the chart is approximate there (decidingNote says so beside the widget).
   */
  deciding?: boolean;
  /**
   * Set only by displayLiveTagData, where the org's units converted this tag: the unit the tag stores. `unit`,
   * `latest` and `series` are then in the shown unit; anything else read for the same tag (a control chart's limits)
   * is in this one.
   */
  storedUnit?: string;
}

/** Keyed by the lower-cased bound key (which is the tag name). */
export type LiveTagData = Record<string, LiveTagDatum>;

/** The last point that carries a label (an enum or label tag's /values point), or null when none does. Pure. */
export function lastLabel(points: readonly TagValuesPoint[] | undefined): { t: string; label: string } | null {
  for (let i = (points?.length ?? 0) - 1; i >= 0; i--) {
    const p = points![i];
    if (p.label != null) return { t: p.t, label: p.label };
  }
  return null;
}

/** A tag whose readings are text (value_text): its latest is a label, never a number. */
function isTextKind(dataKind: string): boolean {
  return dataKind === "categorical_enum" || dataKind === "string_label";
}

/**
 * A text tag's latest label. The batched latest carries its last reading's time but not its text, and a /values point
 * is the most frequent label in its bucket (a day of them over a year's lookback), so the label is read from the
 * second around that time: the last reading's own. With no such time (a pinned window, or no reading in the batched
 * latest's reach) it is the last bucket's label in `points`.
 */
export async function latestText(
  tagId: string,
  points: readonly TagValuesPoint[] | undefined,
  lastTime: string | null,
): Promise<{ t: string; label: string } | null> {
  const at = lastTime ? Date.parse(lastTime) : Number.NaN;
  if (lastTime && Number.isFinite(at) && at > 0) {
    const around = await tags
      .values(tagId, { from: new Date(at - 1000).toISOString(), to: new Date(at + 1000).toISOString(), interval: "1s" })
      .catch(() => null);
    const own = lastLabel(around?.points);
    if (own) return { t: lastTime, label: own.label };
  }
  return lastLabel(points);
}

/**
 * The live readings as the org reads them (units slice 2b): each tag whose unit the org's choice converts has its
 * latest value, its series and its unit moved together, so no chart shows the data in one unit and the axis in another.
 * A gap stays a gap. Where nothing converts (metric, no choice, units the choice shows as recorded) it is the very same
 * object, so a metric org's widgets get exactly what they always got. Pure.
 */
export function displayLiveTagData(byKey: LiveTagData, prefs: DisplayPrefs | undefined): LiveTagData {
  let any = false;
  const out: LiveTagData = {};
  for (const [key, d] of Object.entries(byKey)) {
    const s = shownFor(d.unit, prefs);
    if (!s.converted) {
      out[key] = d;
      continue;
    }
    any = true;
    out[key] = {
      ...d,
      unit: s.unit,
      storedUnit: d.unit,
      latest: d.latest === null ? null : s.convert(d.latest),
      series: d.series.map((p) => (p.v === null || !Number.isFinite(p.v) ? p : { ...p, v: s.convert(p.v) })),
    };
  }
  return any ? out : byKey;
}

/**
 * A per-widget time window token. Drives the /values query's from/to + a
 * coarse bucket interval. `undefined` → the wide {@link DEFAULT_RANGE}, so a
 * sparse sensor/manual tag still plots instead of hitting the backend's
 * 1-hour default window and rendering an empty series.
 */
export type RangeToken = "1h" | "6h" | "24h" | "7d" | "30d" | "1y" | "all";

/** All range tokens, in display order. Also validates a saved `config.range`. */
export const RANGE_TOKENS: readonly RangeToken[] = ["1h", "6h", "24h", "7d", "30d", "1y", "all"];

/** Wide default when a widget hasn't pinned a range (≈ the old manual window). */
export const DEFAULT_RANGE: RangeToken = "1y";

/**
 * Lookback (days) + a coarse bucket interval per token. The backend caps a
 * values query at ~100 buckets (≤ 1-day granularity) and downsamples across
 * the whole [from,to] span, so a wide window with a coarse interval is safe —
 * it fills `series` for sparse sensor tags that the default 1-hour window
 * left empty. The hour windows keep near a hundred points too (60 and 72), so
 * a fast tag still draws its shape there.
 */
const RANGE_WINDOWS: Record<RangeToken, { days: number; interval: string }> = {
  "1h": { days: 1 / 24, interval: "1 minute" },
  "6h": { days: 6 / 24, interval: "5 minutes" },
  "24h": { days: 1, interval: "15 minutes" },
  "7d": { days: 7, interval: "2 hours" },
  "30d": { days: 30, interval: "8 hours" },
  "1y": { days: 365, interval: "1 day" },
  all: { days: 3650, interval: "1 day" },
};

/** A range token's lookback in days (the lots rollup reads the same window as the tags). */
export function rangeLookbackDays(token: RangeToken): number {
  return RANGE_WINDOWS[token].days;
}

/**
 * A fixed window of days a widget can pin instead of a lookback (`config.window`): the data's own period, for a plant
 * whose history ended before today, so an imported August reads as August and not as an empty "last 30 days".
 * Inclusive plant days (YYYY-MM-DD), read on the plant's clock as the lots rollup reads them (plantDayStartMs).
 */
export interface WidgetWindow {
  from: string;
  to: string;
}

const WINDOW_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** The tag-values route reads at most 400 days (platform-core iot/tags_values_slices.go). */
const MAX_WINDOW_DAYS = 400;
const DAY_MS = 86_400_000;

/**
 * The span a widget's readings were read over, in epoch ms: a pinned window's plant days, or a lookback's. anchored
 * says a lookback counted back from the tags' last reading (lookbackSpan).
 */
export interface LiveSpan {
  from: number;
  to: number;
  anchored: boolean;
}

/**
 * A lookback's span: the days before now, or, when no reading of the widget's tags falls in them (a plant whose history
 * ended before today), the days before its last reading, so "7d" shows the data's last week and not an empty one (the
 * reports' presets count back the same way). The last reading itself is in the span. Pure.
 */
export function lookbackSpan(now: number, lastReadingMs: number | null, days: number): LiveSpan {
  const lookback = days * DAY_MS;
  if (lastReadingMs != null && Number.isFinite(lastReadingMs) && lastReadingMs > 0 && lastReadingMs < now - lookback) {
    const to = lastReadingMs + 1;
    return { from: to - lookback, to, anchored: true };
  }
  return { from: now - lookback, to: now, anchored: false };
}

/** Read + validate a widget's pinned window: two real days, in order, at most 400 days in all. */
export function readWidgetWindow(config?: Record<string, unknown>): WidgetWindow | undefined {
  const w = config?.window;
  if (!w || typeof w !== "object") return undefined;
  const { from, to } = w as { from?: unknown; to?: unknown };
  if (typeof from !== "string" || typeof to !== "string" || !WINDOW_DAY.test(from) || !WINDOW_DAY.test(to)) {
    return undefined;
  }
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b) || b < a || (b - a) / DAY_MS + 1 > MAX_WINDOW_DAYS) return undefined;
  // A real day round-trips (no 2026-02-31).
  if (new Date(a).toISOString().slice(0, 10) !== from || new Date(b).toISOString().slice(0, 10) !== to) return undefined;
  return { from, to };
}

/**
 * A pinned window's /values query: its plant days' span on the plant's clock (the lots rollup on the same board reads
 * the same days, #994 review MEDIUM-1; UTC days with no clock), and a bucket interval that keeps it near a hundred
 * points.
 */
export function windowValuesParams(w: WidgetWindow, pt?: PlantTime | null): { from: string; to: string; interval: string } {
  const start = plantDayStartMs(w.from, pt);
  const end = plantDayStartMs(nextPlantDay(w.to), pt);
  const days = (Date.parse(`${w.to}T00:00:00Z`) - Date.parse(`${w.from}T00:00:00Z`)) / DAY_MS + 1;
  const interval = days <= 2 ? "15 minutes" : days <= 10 ? "2 hours" : days <= 45 ? "8 hours" : "1 day";
  return { from: new Date(start).toISOString(), to: new Date(end).toISOString(), interval };
}

/**
 * A pinned window's latest: its series' last reading, never one taken after the window (#994 review, MEDIUM-3). Pure.
 */
export function windowLatest(series: readonly TimeseriesPoint[]): { v: number; t: string } | null {
  for (let i = series.length - 1; i >= 0; i--) {
    const p = series[i];
    if (p.v != null && Number.isFinite(p.v)) return { v: p.v, t: p.t };
  }
  return null;
}

/** A plant day as a date with its year ("3 Aug 2026"), read on the UTC calendar as a window is. */
function dayWithYear(s: string): string {
  return new Date(`${s}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

/** A pinned window's first and last day, each with its year ("1 Mar 2026 to 30 Apr 2026"): what a board says it shows. */
export function windowDates(w: WidgetWindow): string {
  return `${dayWithYear(w.from)} to ${dayWithYear(w.to)}`;
}

/**
 * A pinned window as a chip: a whole calendar month reads "Aug 2026", any other window its first and last day
 * ("3 Aug to 31 Aug"), with the year on both days when the window is not wholly in the current year ("3 Aug 2025 to
 * 31 Aug 2025"), so a chip months old is never read as this year's. Read on the UTC calendar, as the window is; `now`
 * says which year is the current one.
 */
export function windowLabel(w: WidgetWindow, now: Date = new Date()): string {
  const day = (s: string) =>
    new Date(`${s}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  const [fy, fm, fd] = w.from.split("-").map(Number);
  const [ty, tm, td] = w.to.split("-").map(Number);
  const lastOfMonth = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  if (fy === ty && fm === tm && fd === 1 && td === lastOfMonth) {
    return new Date(`${w.from}T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
  }
  const year = now.getUTCFullYear();
  if (fy !== year || ty !== year) return windowDates(w);
  return `${day(w.from)} to ${day(w.to)}`;
}

/** Read + validate a widget's pinned range from its config (`config.range`). */
export function readWidgetRange(
  config?: Record<string, unknown>,
): RangeToken | undefined {
  const r = config?.range;
  return typeof r === "string" && (RANGE_TOKENS as readonly string[]).includes(r)
    ? (r as RangeToken)
    : undefined;
}

/** What a widget's window picker starts from: the lookback and the pinned window its saved config holds. */
export interface RangeSeed {
  range: RangeToken | undefined;
  window: WidgetWindow | undefined;
}

export function rangeSeedOf(config?: Record<string, unknown>): RangeSeed {
  return { range: readWidgetRange(config), window: readWidgetWindow(config) };
}

/**
 * Whether the saved config now says something other than the seed the picker holds (#1675): then the picker re-seeds
 * from it, so a board that changed (a save read back, an Exit that reverted, another editor's window) shows what it
 * holds. Compared by value, so a config that is only a new object (a title edit, a refetch of the same board) keeps a
 * pick the config does not hold, such as a lookback chosen over a pinned window.
 */
export function rangeSeedChanged(a: RangeSeed, b: RangeSeed): boolean {
  return a.range !== b.range || a.window?.from !== b.window?.from || a.window?.to !== b.window?.to;
}

/**
 * A bound key resolved: the tag it reads (for a port fed by several sources, the one the port reads now, which gives the
 * datum its name, its kind and its id), and, for such a port, the series it reads instead of that tag's readings.
 */
export interface ResolvedKey {
  key: string;
  tag: TagOption;
  series?: PortSeriesRef;
}

/**
 * Each bound key's tag: a port the machine's roles resolve reads its port's own tag by id (ports, B6: port-binding.ts),
 * or its one series when several sources feed it and its tag holds numbers (a series is numbers; a text port reads its
 * tag); a port they say has two feeds or is not bound reads none; with `exclusive` (a board card's machine binding) no
 * other key is read. Any other key is read by its name: the org's tag of that name, else, for a port binding, the one
 * tag whose name ends in ".<key>" (the alias's words breaking a tie). A name that the sources of one port share is in
 * ports too (withNamePorts), so it reads the port's series. Pure.
 */
export function resolveBoundTags(
  keys: readonly string[],
  orgTags: readonly TagOption[],
  alias: string,
  ports?: PortTags,
): ResolvedKey[] {
  const byName = new Map<string, TagOption>();
  const byId = new Map<string, TagOption>();
  for (const t of orgTags) {
    if (t.name) byName.set(t.name.toLowerCase(), t);
    byId.set(t.id.toLowerCase(), t);
  }
  // A PORT binding's key is the bare port/param name (e.g. "cooling_time"), but the live tag is gateway-prefixed
  // ("cooling_2.cooling_time"): it resolves by the ".param" suffix where the machine's roles do not resolve it. Exact
  // tag-name match always wins. When a param is shared by PARALLEL gateways ("cooling_1" / "cooling_2") the suffix is
  // ambiguous, so we disambiguate by the widget's alias ("Cooling Line 2"), picking the candidate whose gateway prefix
  // shares the most alias tokens.
  const aliasTokens = new Set(alias.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
  const bySuffix = new Map<string, TagOption[]>();
  for (const t of byName.values()) {
    const dot = t.name.lastIndexOf(".");
    if (dot >= 0) {
      const suf = t.name.slice(dot + 1).toLowerCase();
      const arr = bySuffix.get(suf) ?? [];
      arr.push(t);
      bySuffix.set(suf, arr);
    }
  }
  const pickBySuffix = (cands: TagOption[] | undefined): TagOption | undefined => {
    if (!cands || cands.length === 0) return undefined;
    if (cands.length === 1) return cands[0];
    if (aliasTokens.size === 0) return undefined; // ambiguous, no alias to disambiguate
    let best: TagOption | undefined;
    let bestScore = 0;
    let tie = false;
    for (const t of cands) {
      const dot = t.name.lastIndexOf(".");
      const prefixTokens = (dot >= 0 ? t.name.slice(0, dot) : t.name)
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
      let score = 0;
      for (const tok of prefixTokens) if (aliasTokens.has(tok)) score++;
      if (score > bestScore) {
        bestScore = score;
        best = t;
        tie = false;
      } else if (score === bestScore && score > 0) {
        tie = true;
      }
    }
    return tie ? undefined : best; // a clear winner only
  };
  const out: ResolvedKey[] = [];
  for (const k of keys) {
    const port = ports?.byKey.get(k);
    let tag: TagOption | undefined;
    let series: PortSeriesRef | undefined;
    if (port) {
      // The roles' own tag, by id; the org's list names it, else the roles do (a tag past the list's reach).
      tag = port.kind === "tag"
        ? (byId.get(port.tagId.toLowerCase()) ??
          { id: port.tagId, device_id: "", name: port.tagName, unit: port.role.unit, data_kind: "" })
        : undefined;
      if (tag && port.kind === "tag" && port.series && !isTextKind(tag.data_kind)) series = port.series;
    } else if (!ports?.exclusive) {
      const kl = k.toLowerCase();
      tag = byName.get(kl) ?? pickBySuffix(bySuffix.get(kl));
    }
    if (tag) out.push(series ? { key: k, tag, series } : { key: k, tag });
  }
  return out;
}

/** What a widget's readings are read over: its lookback, or its pinned window of plant days on the plant's clock. */
export interface LiveReadWindow {
  range: RangeToken;
  dataWindow?: WidgetWindow;
  clock?: PlantTime | null;
}

/** A read of a widget's keys. `failed` names the keys (lower case) that got no answer, which is not an empty answer. */
export interface LiveRead {
  byKey: LiveTagData;
  span: LiveSpan;
  failed: string[];
}

/** A read worth asking again: the network, the server, or its rate limit. Any other refusal will not change by itself. */
function mayAnswerLater(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return typeof status !== "number" || status >= 500 || status === 429;
}

/**
 * A port's series as a widget's datum (S3): its points, in the series' unit (the chart's, else the roles'), its latest
 * the port's current value (a pinned window's: its own last point), named by the tag the port reads now. A chart that
 * could not be read gives no points and no latest, as a tag's failed read does. Pure.
 */
export function portSeriesDatum(
  tag: TagOption,
  series: PortSeriesRef,
  chart: PortSeries | null,
  pinned: boolean,
): LiveTagDatum {
  const points = (chart?.points ?? [])
    .filter((p) => p.value != null && Number.isFinite(p.value))
    .map((p) => ({ t: p.t, v: p.value }));
  const last = pinned ? windowLatest(points) : null;
  const now = chart?.latest ?? null;
  return {
    tagId: tag.id,
    name: tag.name,
    ...(tag.label && { label: tag.label }),
    ...(tag.machine && { machine: tag.machine }),
    unit: chart?.unit || series.unit || tag.unit || "",
    dataKind: tag.data_kind,
    latest: pinned ? (last?.v ?? null) : (now?.value ?? null),
    latestTime: pinned ? (last?.t ?? null) : (now?.t ?? null),
    series: points,
    // A window of more than 500 readings comes as bucket means ("avg"), a smaller one as each reading ("raw").
    ...(chart?.agg === "avg" && chart.bucket_seconds > 0 && { bucketMs: chart.bucket_seconds * 1000 }),
    ...(chart?.deciding && { deciding: true }),
  };
}

/**
 * Reads the widget's resolved keys over its window: one batched latest for every tag read (and every source of a port
 * read as its series, which only places the lookback), then each tag's /values, or each port's series route. A lookback
 * counts back from now, or from the last reading when none falls in it (lookbackSpan).
 */
export async function readLiveTagData(
  resolved: readonly ResolvedKey[],
  { range, dataWindow, clock }: LiveReadWindow,
): Promise<LiveRead> {
  // Widen the /values window for EVERY tag (not just manual): sensor tags
  // (e.g. VSync) are frequently sparse too — a few readings a day — so the
  // backend's default 1-hour window leaves `series` empty and the chart /
  // history table show "No data". A wide from/to + a coarse interval (≤ 1
  // day; the backend caps ~100 buckets and downsamples the span) fills the
  // series for all sources. NB: Date.now() lives here, inside the queryFn,
  // never in a component render — the repo's react-hooks purity rule flags
  // Date.now() in render, so the window is keyed by `range` instead.
  const { days, interval } = RANGE_WINDOWS[range];
  // ONE batched latest-value request for EVERY tag on the dashboard —
  // replaces the N separate /latest calls (the per-widget × per-tag fan-out
  // that made data-heavy dashboards slow). Series stay per-tag for now (each
  // is already windowed + rides the readings index); batching the /values
  // fan-out is the next step. A port read as its series asks for its sources'
  // latest readings only to place the lookback: its own latest is its series'.
  // A pinned window is history: its latest is the window's own last reading (below), so no /latest call.
  const latestIds = [...new Set(resolved.flatMap((r) => (r.series ? r.series.sources : [r.tag.id])))];
  const failed = new Set<string>();
  const unanswered = (key: string) => (err: unknown) => {
    if (mayAnswerLater(err)) failed.add(key.toLowerCase());
    return null;
  };
  const latestList = dataWindow || latestIds.length === 0
    ? null
    : await tags.batchLatest(latestIds).catch((err: unknown) => {
        // The one latest read speaks for every key that took part in it.
        for (const { key } of resolved) unanswered(key)(err);
        return null;
      });
  const latestById = new Map(
    (latestList?.items ?? []).map((it) => [it.tag_id, it]),
  );
  // A lookback counts back from now, or from the tags' last reading when none falls in it (lookbackSpan).
  let valuesParams: { from: string; to: string; interval: string };
  let span: LiveSpan;
  if (dataWindow) {
    valuesParams = windowValuesParams(dataWindow, clock);
    span = { from: Date.parse(valuesParams.from), to: Date.parse(valuesParams.to), anchored: false };
  } else {
    const times = (latestList?.items ?? [])
      .map((it) => (it.time ? Date.parse(it.time) : Number.NaN))
      .filter((t) => Number.isFinite(t) && t > 0);
    span = lookbackSpan(Date.now(), times.length > 0 ? Math.max(...times) : null, days);
    valuesParams = { from: new Date(span.from).toISOString(), to: new Date(span.to).toISOString(), interval };
  }
  const entries = await Promise.all(
    resolved.map(async ({ key, tag, series: port }) => {
      if (port) {
        const chart = await readPortSeries(port.entityId, port.port, {
          from: valuesParams.from,
          to: valuesParams.to,
          bucket: valuesParams.interval,
        }).catch(unanswered(key));
        return [key.toLowerCase(), portSeriesDatum(tag, port, chart, !!dataWindow)] as const;
      }
      const values = await tags.values(tag.id, valuesParams).catch(unanswered(key));
      const latest = latestById.get(tag.id);
      const hasReading =
        !!latest &&
        latest.value != null &&
        !!latest.time &&
        new Date(latest.time).getTime() > 0;
      const series = (values?.points ?? [])
        .filter((p) => p.value != null)
        .map((p) => ({ t: p.t, v: p.value ?? null }));
      const last = dataWindow ? windowLatest(series) : null;
      // The route answers with each reading ("raw") up to 500 of them in the window, else with bucket means.
      const bucketMs = bucketWidthMs(values?.interval);
      const label = isTextKind(tag.data_kind)
        ? await latestText(tag.id, values?.points, dataWindow ? null : (latest?.time ?? null))
        : undefined;
      const datum: LiveTagDatum = {
        tagId: tag.id,
        name: tag.name,
        ...(tag.label && { label: tag.label }),
        ...(tag.machine && { machine: tag.machine }),
        unit: tag.unit ?? "",
        dataKind: tag.data_kind,
        latest: dataWindow ? (last?.v ?? null) : hasReading ? latest!.value : null,
        latestTime: dataWindow
          ? (last?.t ?? label?.t ?? null)
          : hasReading
            ? latest!.time
            : (label?.t ?? null),
        series,
        ...(bucketMs && { bucketMs }),
        ...(label !== undefined && { latestLabel: label?.label ?? null }),
      };
      return [key.toLowerCase(), datum] as const;
    }),
  );
  return { byKey: Object.fromEntries(entries) as LiveTagData, span, failed: [...failed] };
}

/** The waits before a first read asks again for the keys that got no answer. About one poll in all. */
const FIRST_READ_RETRIES_MS = [1000, 3000];

const pause = (ms: number) => new Promise((done) => setTimeout(done, ms));

/**
 * A read that failed draws as if there were nothing: "No readings" over a tag that has readings. A widget's first read
 * asks again for those keys, so its outline stays until they answer or the tries run out; a poll keeps what the widget
 * has for them and leaves them to the next poll, so one failed poll does not blank a chart that was drawn.
 */
export async function readThroughFailures(
  resolved: readonly ResolvedKey[],
  window: LiveReadWindow,
  held?: LiveTagData,
): Promise<LiveRead> {
  let read = await readLiveTagData(resolved, window);
  if (held) {
    const kept = read.failed.flatMap((key) => (held[key] ? [[key, held[key]] as const] : []));
    return { ...read, byKey: { ...read.byKey, ...Object.fromEntries(kept) } };
  }
  for (const wait of FIRST_READ_RETRIES_MS) {
    if (read.failed.length === 0) break;
    await pause(wait);
    const unanswered = new Set(read.failed);
    const again = await readLiveTagData(resolved.filter((r) => unanswered.has(r.key.toLowerCase())), window);
    read = { ...read, byKey: { ...read.byKey, ...again.byKey }, failed: again.failed };
  }
  return read;
}

export function useLiveTagData(
  ds?: DataSource,
  range?: RangeToken,
  dataWindow?: WidgetWindow,
  ports?: PortTags,
): { byKey: LiveTagData; hasLive: boolean; span?: LiveSpan; loading: boolean } {
  const orgTags = useQuery({
    queryKey: ["org-tags"],
    queryFn: listOrgTags,
    enabled: isLiveApi,
    staleTime: 30_000,
  });

  // Bound keys across every slot: a tag binding's key is the tag's name, a port binding's a machine's port or
  // parameter. A port the machine's roles resolve reads its own tag by id (ports: port-binding.ts); any other key is
  // read by its name (resolveBoundTags).
  const boundKeys = useMemo(() => {
    if (!ds || (ds.kind !== "tag" && ds.kind !== "port") || !ds.slots) return [] as string[];
    return boundKeysOf(ds);
  }, [ds]);

  const resolved = useMemo(
    () => resolveBoundTags(boundKeys, orgTags.data?.items ?? [], ds?.alias ?? "", ports),
    [boundKeys, orgTags.data, ds, ports],
  );

  // Stable query key so polling de-dupes per tag-set + window. Each key is in it with what it reads (a tag by its id, a
  // port read as its series by its machine and port): the answer is keyed by the widget's keys, so two widgets that read
  // one tag or one port under two keys (a port and its tag's name) must not share it.
  const idsKey = resolved
    .map((r) => JSON.stringify([r.key.toLowerCase(), r.series ? [r.series.entityId, r.series.port] : r.tag.id]))
    .sort()
    .join(",");
  const rangeToken = range ?? DEFAULT_RANGE;
  const windowKey = dataWindow ? `${dataWindow.from}..${dataWindow.to}` : "";
  // A pinned window's days are plant days: read on the plant's clock, once it is known.
  const plantTime = useQuery({
    queryKey: ["plant-time"],
    queryFn: plantTimeApi.get,
    enabled: isLiveApi && !!dataWindow,
    staleTime: 5 * 60_000,
  });
  const clock = dataWindow ? plantTime.data : undefined;
  const clockKey = clock ? `${clock.timezone}|${clock.production_day_start}` : "";

  const live = useQuery({
    queryKey: ["dashboard-live-tags", idsKey, rangeToken, windowKey, clockKey],
    enabled: isLiveApi && resolved.length > 0 && (!dataWindow || !plantTime.isLoading),
    // A pinned window is history: nothing new arrives in it, so it is not polled.
    refetchInterval: dataWindow ? false : 7000,
    queryFn: ({ client, queryKey }) =>
      readThroughFailures(
        resolved,
        { range: rangeToken, dataWindow, clock },
        client.getQueryData<LiveRead>(queryKey)?.byKey,
      ),
  });

  // On their way, not missing. The tags come first (the read waits for them, so a read that has not started says
  // nothing), then a pinned window's plant clock, then the readings. A key no tag answers to reads nothing, and a poll
  // keeps what it has: neither is loading. The clock counts only for a pinned window: a disabled query still reads as
  // loading while any other widget on the page reads the same key, which brought every outline back on a drawn board.
  const loading =
    boundKeys.length > 0 && (orgTags.isLoading || (!!dataWindow && plantTime.isLoading) || live.isLoading);

  return { byKey: live.data?.byKey ?? {}, hasLive: isLiveApi && resolved.length > 0, span: live.data?.span, loading };
}
